import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, requestWithMessage } from '../lib/api';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { intervalText, useMonitorStatus, useMonitoringDefaults, type MonitoringSettingsInfo } from '../lib/monitoring';
import { relativeTime } from '../lib/format';
import { FormGrid, FormPage } from '../components/FormPage';
import { Checkbox, Field, Input, Select, useToast } from '../components/ui';

/** The numbers on the page, with the range the API takes for each. */
const NUMBERS: Array<{ key: string; label: TranslationKey; hint?: TranslationKey; min: number; max: number; step?: number }> = [
  { key: 'monitoring.defaultTimeoutSeconds', label: 'monitoring.settings.timeout', min: 1, max: 60 },
  { key: 'monitoring.failuresToConfirm', label: 'monitoring.settings.failures', hint: 'monitoring.settings.failuresHint', min: 1, max: 10 },
  { key: 'monitoring.retrySeconds', label: 'monitoring.settings.retry', hint: 'monitoring.settings.retryHint', min: 5, max: 300 },
  { key: 'monitoring.successesToRecover', label: 'monitoring.settings.successes', hint: 'monitoring.settings.successesHint', min: 1, max: 10 },
  { key: 'monitoring.slowResponseMs', label: 'monitoring.settings.slow', min: 100, max: 60_000, step: 100 },
  { key: 'monitoring.sslWarnDays', label: 'monitoring.settings.sslWarn', hint: 'monitoring.settings.sslWarnHint', min: 1, max: 90 },
  { key: 'monitoring.checkRetentionDays', label: 'monitoring.settings.retention', hint: 'monitoring.settings.retentionHint', min: 1, max: 365 },
];

/**
 * Route: `/monitoring/settings` (operator's spec, 2026-10-20: check intervals and
 * alert limits adjustable from the admin). Each URL may set its own figures;
 * these are what the rest take. The administrators' (`monitoring.manage`).
 */
export function MonitoringSettingsPage() {
  const { t, locale } = useI18n();
  const toast = useToast();
  const queryClient = useQueryClient();
  const defaults = useMonitoringDefaults();
  const status = useMonitorStatus();
  const watchdogSeenAt = status.data?.watchdogSeenAt ?? null;
  // The watchdog reads every minute; quiet for five, it has stopped (the API's WATCHDOG_FRESH_MS).
  const watchdogReading = !!watchdogSeenAt && Date.now() - new Date(watchdogSeenAt).getTime() < 5 * 60_000;
  const settings = useQuery({
    queryKey: ['monitoring', 'settings'],
    queryFn: () => api.get<MonitoringSettingsInfo>('/monitoring/settings'),
  });

  const [values, setValues] = useState<Record<string, string | number | boolean>>({});
  const [loaded, setLoaded] = useState(false);
  if (settings.data && !loaded) {
    setLoaded(true);
    setValues(settings.data.values);
  }
  const set = (key: string, value: string | number | boolean) => setValues({ ...values, [key]: value });

  const save = useMutation({
    mutationFn: () => {
      // Numbers go as numbers; the watchdog's address as typed.
      const body = Object.fromEntries(
        Object.entries(values).map(([key, value]) => [key, typeof value === 'string' && key !== 'monitoring.heartbeatUrl' ? Number(value) : value]),
      );
      return requestWithMessage<MonitoringSettingsInfo>('/monitoring/settings', { method: 'PUT', body: { values: body } });
    },
    onSuccess: ({ data }) => {
      queryClient.setQueryData(['monitoring', 'settings'], data);
      void queryClient.invalidateQueries({ queryKey: ['monitoring', 'defaults'] });
      setValues(data.values);
      toast.success(t('monitoring.settings.saved'));
    },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <FormPage
      title={t('monitoring.settings.title')}
      subtitle={t('monitoring.settings.subtitle')}
      backTo="/monitoring"
      backLabel={t('nav.groupMonitoring')}
      submitLabel={t('common.save')}
      submitting={save.isPending}
      loading={settings.isLoading}
      error={settings.error}
      onRetry={() => void settings.refetch()}
      onSubmit={() => save.mutate()}
      width="lg"
    >
      <FormGrid>
        <p className="text-body-sm font-medium text-[var(--text-secondary)] sm:col-span-2">{t('monitoring.settings.checks')}</p>
        <Field label={t('monitoring.settings.interval')}>
          <Select
            value={String(values['monitoring.defaultIntervalSeconds'] ?? '')}
            onChange={(e) => set('monitoring.defaultIntervalSeconds', Number(e.target.value))}
          >
            {(defaults.data?.intervalChoices ?? [60, 300, 600, 900, 1800]).map((seconds) => (
              <option key={seconds} value={String(seconds)}>{intervalText(seconds, t)}</option>
            ))}
          </Select>
        </Field>
        {NUMBERS.map((n) => (
          <Field key={n.key} label={t(n.label)} hint={n.hint ? t(n.hint) : undefined}>
            <Input
              type="number"
              min={n.min}
              max={n.max}
              step={n.step ?? 1}
              value={String(values[n.key] ?? '')}
              onChange={(e) => set(n.key, e.target.value)}
            />
          </Field>
        ))}

        <p className="mt-2 border-t hairline pt-4 text-body-sm font-medium text-[var(--text-secondary)] sm:col-span-2">
          {t('monitoring.settings.alerts')}
        </p>
        <div className="flex flex-col gap-2 sm:col-span-2">
          <Checkbox
            checked={values['monitoring.alertsEnabled'] === true}
            onChange={(e) => set('monitoring.alertsEnabled', e.target.checked)}
            label={t('monitoring.settings.alertsEnabled')}
          />
          <Checkbox
            checked={values['monitoring.bellEnabled'] === true}
            onChange={(e) => set('monitoring.bellEnabled', e.target.checked)}
            label={t('monitoring.settings.bellEnabled')}
          />
        </div>
        <Field label={t('monitoring.settings.reminder')} hint={t('monitoring.settings.reminderHint')}>
          <Input
            type="number"
            min={0}
            max={1440}
            value={String(values['monitoring.reminderMinutes'] ?? '')}
            onChange={(e) => set('monitoring.reminderMinutes', e.target.value)}
          />
        </Field>

        <p className="mt-2 border-t hairline pt-4 text-body-sm font-medium text-[var(--text-secondary)] sm:col-span-2">
          {t('monitoring.settings.watchdog')}
        </p>
        <p className="text-caption text-faint sm:col-span-2">{t('monitoring.settings.watchdogHint')}</p>
        {status.data && (
          <p
            className={`text-body-sm sm:col-span-2 ${
              watchdogReading ? 'text-success-fg dark:text-success' : 'text-warning-fg dark:text-warning-dark'
            }`}
          >
            {watchdogReading
              ? t('monitoring.settings.watchdogReading', {
                  when: relativeTime(watchdogSeenAt!, locale),
                  from: status.data.watchdogFrom ? `Cloudflare ${status.data.watchdogFrom}` : 'Cloudflare',
                })
              : watchdogSeenAt
                ? t('monitoring.settings.watchdogStopped', { when: relativeTime(watchdogSeenAt, locale) })
                : t('monitoring.settings.watchdogNever')}
          </p>
        )}
        <Field label={t('monitoring.settings.heartbeatUrl')} className="sm:col-span-2">
          <Input
            dir="ltr"
            value={String(values['monitoring.heartbeatUrl'] ?? '')}
            placeholder="https://hc-ping.com/…"
            onChange={(e) => set('monitoring.heartbeatUrl', e.target.value)}
          />
        </Field>
      </FormGrid>
    </FormPage>
  );
}
